import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Package, Truck, PackagePlus, PackageMinus, BarChart3, LogOut, TruckIcon, HandCoins, Wrench, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const isLoginPage = location.pathname === '/login';
  const isHomePage = location.pathname === '/';
  const isAlmacenPage = location.pathname === '/almacen';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Función para determinar si un link está activo
  const isActive = (path) => {
    // Para rutas específicas
    if (path === location.pathname) return true;
    // Para rutas que empiezan con el path (como /inventory, /inventory/product/123)
    if (location.pathname.startsWith(path) && path !== '/') return true;
    return false;
  };

  // Función para obtener las clases del link según si está activo
  const getLinkClasses = (path, baseColor, activeColor) => {
    const base = "flex items-center gap-1 font-medium transition-colors duration-200";
    if (isActive(path)) {
      return `${base} ${activeColor} font-bold`;
    }
    return `${base} text-gray-600 hover:${activeColor}`;
  };

  return (
    <div className={`min-h-screen ${isLoginPage ? '' : 'bg-gradient-to-br from-gray-50 to-gray-100'} text-gray-900`}>
      {!isLoginPage && (
        <motion.header
          className="bg-white/80 backdrop-blur-lg shadow-sm py-2 px-6 border-b border-gray-200"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 120, damping: 14 }}
        >
        <div className="w-full flex justify-between items-center">
          <Link to={isAlmacenPage ? "/almacen" : "/"} className="flex items-center gap-3 text-2xl font-bold text-gray-800 ml-4">
            {!isHomePage && (
              <>
                <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">SistemaDeAlmacen</span>
                <img 
                  src="/images/TITSA-LOGO-negro-sin-letras.png" 
                  alt="TITSA Logo" 
                  className="w-24 h-24 object-contain"
                />
              </>
            )}
          </Link>
          <nav className="mr-4">
            <ul className="flex space-x-6">
              {!isHomePage && !isAlmacenPage && (
                <>
                  <li>
                    <Link
                      to="/inventory"
                      className={getLinkClasses('/inventory', 'blue', 'text-blue-600')}
                    >
                      <Package className="w-4 h-4" /> Inventario
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/suppliers"
                      className={getLinkClasses('/suppliers', 'green', 'text-green-600')}
                    >
                      <Truck className="w-4 h-4" /> Proveedores
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/entries"
                      className={getLinkClasses('/entries', 'cyan', 'text-cyan-600')}
                    >
                      <PackagePlus className="w-4 h-4" /> Entradas
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/exits"
                      className={getLinkClasses('/exits', 'pink', 'text-pink-600')}
                    >
                      <PackageMinus className="w-4 h-4" /> Salidas
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/summary"
                      className={getLinkClasses('/summary', 'indigo', 'text-indigo-600')}
                    >
                      <BarChart3 className="w-4 h-4" /> Resumen
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/loans"
                      className={getLinkClasses('/loans', 'yellow', 'text-yellow-600')}
                    >
                      <HandCoins className="w-4 h-4" /> Préstamos
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/maintenances"
                      className={getLinkClasses('/maintenances', 'purple', 'text-purple-600')}
                    >
                      <Wrench className="w-4 h-4" /> Mantenimientos
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/fleet-tracking"
                      className={getLinkClasses('/fleet-tracking', 'emerald', 'text-emerald-600')}
                    >
                      <MapPin className="w-4 h-4" /> Fleet Tracking
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/fleet"
                      className={getLinkClasses('/fleet', 'orange', 'text-orange-600')}
                    >
                      <TruckIcon className="w-4 h-4" /> Estado Flota
                    </Link>
                  </li>
                </>
              )}
              {isAuthenticated && (
                <li>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1 text-gray-600 hover:text-red-600 font-medium transition-colors duration-200"
                  >
                    <LogOut className="w-4 h-4" /> Cerrar Sesión
                  </button>
                </li>
              )}
              {isAuthenticated && user && (
                <li className="flex items-center gap-2 text-gray-700 font-medium">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {user.username?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm">{user.username}</span>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </motion.header>
      )}
      <main className={isLoginPage ? '' : 'w-full'}>
        {children}
      </main>
      {!isLoginPage && (
        <motion.footer
          className="bg-white/80 backdrop-blur-lg shadow-inner py-4 px-6 mt-8 border-t border-gray-200 text-center text-gray-500 text-sm"
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.2 }}
        >
          <p>&copy; {new Date().getFullYear()} SistemaDeAlmacen. Todos los derechos reservados.</p>
        </motion.footer>
      )}
    </div>
  );
};

export default Layout;